/** How the month calendar marks a day with an item: a filled dot, as for a session, or a ring. */
export type CalendarMarker = 'dot' | 'ring'

/** Something dated, from any feature, for the week and month widgets. */
export interface DatedItem {
  readonly title: string
  readonly start: Date
  readonly end: Date
  /** Where the item links to. */
  readonly href: string
  /** Its mark on the month calendar; a dot when not given. */
  readonly marker?: CalendarMarker
}

/** One line of the month calendar's legend: a mark and what it means. */
export interface CalendarLegendEntry {
  readonly marker: CalendarMarker
  readonly label: string
}

/** A calendar day, `YYYY-MM-DD`. */
export type CalendarDay = string

/**
 * The viewer's locale and time zone. Without them, day and month names follow the document's
 * language (`<html lang>`) and times the browser's time zone.
 */
export interface CalendarFormat {
  readonly locale?: string | undefined
  readonly timeZone?: string | undefined
}

/** `locale`, else the document's language, else `undefined`, which is the browser's. */
export function resolveLocale(locale: string | undefined): string | undefined {
  return locale ?? (document.documentElement.lang || undefined)
}

/** One numeric date formatter per time zone, since building one is the slow part. */
const dayFormatters = new Map<string, Intl.DateTimeFormat>()

/** The calendar day `instant` falls on in `timeZone`. */
export function dayOf(instant: Date, timeZone: string | undefined): CalendarDay {
  const key = timeZone ?? ''
  let formatter = dayFormatters.get(key)
  if (formatter === undefined) {
    // A fixed locale only to read the numeric parts back; nothing it formats is shown.
    formatter = new Intl.DateTimeFormat('en-US', {
      ...(timeZone === undefined ? {} : { timeZone }),
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
    dayFormatters.set(key, formatter)
  }
  const parts = formatter.formatToParts(instant)
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((candidate) => candidate.type === type)?.value ?? ''
  return `${part('year')}-${part('month')}-${part('day')}`
}

/** Noon UTC on `day`, so formatting it with `timeZone: 'UTC'` gives that day in any locale. */
export function utcNoon(day: CalendarDay): Date {
  const [year = 0, month = 1, date = 1] = day.split('-').map(Number)
  return new Date(Date.UTC(year, month - 1, date, 12))
}

/** `day` moved by `count` days. */
export function addDays(day: CalendarDay, count: number): CalendarDay {
  const date = utcNoon(day)
  date.setUTCDate(date.getUTCDate() + count)
  return date.toISOString().slice(0, 10)
}

/** How many days a month grid shows: six weeks. */
export const monthGridDays = 42

/** The first day a month grid shows for `month` (`YYYY-MM`): the Sunday on or before its first day. */
export function monthGridStart(month: string): CalendarDay {
  const first = `${month}-01`
  return addDays(first, -utcNoon(first).getUTCDay())
}

/** An item with the first and last days it covers in the viewer's time zone. */
export interface PlacedItem extends DatedItem {
  /** Unique within one placement; items carry no id of their own. */
  readonly key: string
  readonly firstDay: CalendarDay
  readonly lastDay: CalendarDay
}

/** Places each item on the days it covers, earliest first. */
export function placeItems(
  items: readonly DatedItem[],
  timeZone: string | undefined,
): readonly PlacedItem[] {
  return items
    .toSorted((a, b) => a.start.getTime() - b.start.getTime())
    .map((item, position) => ({
      title: item.title,
      start: item.start,
      end: item.end,
      href: item.href,
      marker: item.marker ?? 'dot',
      key: String(position),
      firstDay: dayOf(item.start, timeZone),
      // The end is not part of the item, so one ending at midnight stays on the day before.
      lastDay: dayOf(item.end > item.start ? new Date(item.end.getTime() - 1) : item.end, timeZone),
    }))
}

/** Whether `item` falls on `day`. Days compare as strings because they are `YYYY-MM-DD`. */
export function covers(item: PlacedItem, day: CalendarDay): boolean {
  return item.firstDay <= day && day <= item.lastDay
}

/** A formatter in the viewer's locale for calendar days, which are noon UTC instants. */
export function dayFormat(
  locale: string | undefined,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat(locale, { ...options, timeZone: 'UTC' })
}

/** A formatter in the viewer's locale and time zone for instants. */
export function instantFormat(
  format: CalendarFormat,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat(format.locale, {
    ...options,
    ...(format.timeZone === undefined ? {} : { timeZone: format.timeZone }),
  })
}
